import { HeidiVersion } from '@heidi/schema/ml-scribe';
import { useAtomValue } from 'jotai';

import {
  HEIDI_2_AWARENESS_BANNER_MODAL_ID,
  HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID,
  HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
  HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
} from '../../contracts/heidi-2';

import {
  heidi2ExploreLaterPreviewAtom,
  heidi2ExploreLaterPreviewStartedAtAtom,
} from './heidi-2-awareness-banner-atoms';
import {
  resolveHeidi2WelcomeOutcome,
  type Heidi2ExploreLaterAudience,
} from './heidi-2-explore-later-eligibility';
import { getHeidi2Ports } from './heidi-2-ports';

export interface Heidi2ExploreLaterState extends Heidi2ExploreLaterAudience {
  isUserManaged: boolean | null | undefined;
  isBannerClosed: boolean;
  isEvidenceTourClosed: boolean;
  isChecklistClosed: boolean;
  /** When the welcome was first seen — or a dev preview started — as an ISO string. */
  welcomeSeenAt: string | undefined;
}

/**
 * Whether any explore-later nudge can show at all: the onboarding flag, or a
 * dev-tools preview. Lets a host keep its plain markup, and skip the queries
 * below, for everyone else.
 */
export const useIsHeidi2ExploreLaterLive = (): boolean => {
  const isHeidi2OnboardingEnabled = getHeidi2Ports().useIsHeidi2OnboardingEnabled();
  const preview = useAtomValue(heidi2ExploreLaterPreviewAtom);
  return isHeidi2OnboardingEnabled || preview !== null;
};

/**
 * Everything the explore-later nudges decide on, read once from the user and
 * billing queries. Reads SaaS billing, so only mount callers on cloud builds —
 * the same reason `Heidi2AwarenessBanner` skips on-prem.
 */
export const useHeidi2ExploreLaterState = (): Heidi2ExploreLaterState => {
  const ports = getHeidi2Ports();
  const isHeidi2OnboardingEnabled = ports.useIsHeidi2OnboardingEnabled();
  const { data: billingInfo } = ports.useUserBillingInfo();
  const { data: user } = ports.useQueryUser((user) =>
    user
      ? {
          isManaged: user.is_managed,
          welcomeView: user.seen_modals?.[HEIDI_2_AWARENESS_BANNER_MODAL_ID],
          bannerView: user.seen_modals?.[HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID],
          tourView: user.seen_modals?.[HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID],
          checklistView: user.seen_modals?.[HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID],
        }
      : undefined
  );

  const preview = useAtomValue(heidi2ExploreLaterPreviewAtom);
  const previewStartedAt = useAtomValue(heidi2ExploreLaterPreviewStartedAtAtom);
  const seenModalIds = ports.useSeenModalIds();
  const audience = preview
    ? {
        isHeidi2OnboardingEnabled: true,
        isHeidi2: true,
        welcomeOutcome: preview,
        isUserManaged: false,
      }
    : {
        isHeidi2OnboardingEnabled,
        isHeidi2: billingInfo?.heidi_version === HeidiVersion.Value2,
        welcomeOutcome: resolveHeidi2WelcomeOutcome(user?.welcomeView),
        isUserManaged: user?.isManaged,
      };

  return {
    ...audience,
    isBannerClosed: ports.isModalClosed(
      HEIDI_2_EXPLORE_LATER_BANNER_MODAL_ID,
      user?.bannerView
    ),
    // The queue's seen set is written the moment the tour closes and survives
    // a rejected save, so the Home dot goes with the tour, not a refresh later.
    isEvidenceTourClosed:
      seenModalIds?.has(HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID) === true ||
      ports.isModalClosed(HEIDI_2_EVIDENCE_TOUR_SPOTLIGHT_MODAL_ID, user?.tourView),
    isChecklistClosed: ports.isModalClosed(
      HEIDI_2_GET_STARTED_CHECKLIST_MODAL_ID,
      user?.checklistView
    ),
    // `first_seen_at` is optional on the record; without an anchor the
    // checklist would show a task that can never tick.
    welcomeSeenAt: preview
      ? (previewStartedAt ?? undefined)
      : (user?.welcomeView?.first_seen_at ?? user?.welcomeView?.last_seen_at),
  };
};
